import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/hooks/use-auth';
import { postService } from '@/services/data';

/**
 * Real per-post analytics for the inspector (F-134). Reads the aggregated
 * `post_metrics_daily` snapshot via PostService.getPostDailyMetrics — the
 * authoritative per-post numbers, not the usually-empty social_posts.metrics
 * jsonb. `hasData` lets the caller distinguish "no analytics yet" from a
 * still-loading state. Pass `enabled: false` to skip the query (e.g. for
 * draft/scheduled posts that have no metrics).
 */
export function usePostMetrics(postId: string | undefined, options?: { enabled?: boolean }) {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: ['post-metrics-daily', postId, user?.id],
    enabled: !!postId && (options?.enabled ?? true),
    staleTime: 60_000,
    queryFn: async () => {
      const res = await postService.getPostDailyMetrics(postId!);
      if (res.error) throw new Error(res.error.message);
      return (
        res.data ?? {
          metrics: {},
          hasData: false,
          byPlatform: {},
          byDestination: {},
          destinations: [],
          coverage: { reportingDestinations: 0, totalDestinations: 0 },
        }
      );
    },
  });

  return {
    metrics: query.data?.metrics ?? {},
    byPlatform: query.data?.byPlatform ?? {},
    byDestination: query.data?.byDestination ?? {},
    destinations: query.data?.destinations ?? [],
    coverage: query.data?.coverage ?? { reportingDestinations: 0, totalDestinations: 0 },
    hasData: query.data?.hasData ?? false,
    isLoading: query.isLoading,
  };
}
